import type { Project } from '@servicetim/shared';
import { useProjects, useUsers } from '@servicetim/ui/api';
import {
  Badge,
  buttonVariants,
  Checkbox,
  type ColumnDef,
  DataTable,
  Label,
  pagedTableProps,
} from '@servicetim/ui/components';
import { hasTenantRole, useSessionClaims } from '@servicetim/ui/lib';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

export interface ProjectsTableProps {
  includeArchived: boolean;
  onIncludeArchivedChange: (next: boolean) => void;
}

/** `/projects` list: key, name, category, lead, access, archived; row click opens the project. */
export function ProjectsTable({ includeArchived, onIncludeArchivedChange }: ProjectsTableProps) {
  const navigate = useNavigate();
  const projects = useProjects({ includeArchived });
  const { tenantRole } = useSessionClaims();
  const isAdmin = hasTenantRole(tenantRole, 'admin');
  // Lead names come from the users list (tenant admins only); others see the id shortened.
  const users = useUsers({ pageSize: 200, enabled: isAdmin });
  const userNames = useMemo(
    () => new Map(users.items.map((u) => [u.id, u.displayName])),
    [users.items],
  );

  const columns = useMemo<ColumnDef<Project>[]>(
    () => [
      {
        accessorKey: 'key',
        header: 'Key',
        size: 110,
        cell: ({ row }) => (
          <Badge variant="outline" className="font-mono">
            {row.original.key}
          </Badge>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to="/projects/$projectId"
            params={{ projectId: row.original.id }}
            className="font-medium text-fg hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ getValue }) =>
          getValue<string | null>() ?? <span className="text-fg-muted">—</span>,
      },
      {
        id: 'lead',
        header: 'Lead',
        enableSorting: false,
        cell: ({ row }) => {
          const id = row.original.leadId;
          if (!id) return <span className="text-fg-muted">—</span>;
          return userNames.get(id) ?? <span className="font-mono text-xs">{id.slice(0, 8)}…</span>;
        },
      },
      {
        id: 'access',
        header: 'Your access',
        enableSorting: false,
        size: 120,
        cell: ({ row }) =>
          row.original.canAdmin ? (
            <Badge variant="info">Admin</Badge>
          ) : (
            <Badge variant="secondary">Member</Badge>
          ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        size: 110,
        cell: ({ row }) =>
          row.original.archivedAt ? (
            <Badge variant="warning">Archived</Badge>
          ) : (
            <Badge variant="success">Active</Badge>
          ),
      },
    ],
    [userNames],
  );

  return (
    <DataTable
      columns={columns}
      getRowId={(p) => p.id}
      {...pagedTableProps(projects)}
      onRowClick={(project) =>
        void navigate({ to: '/projects/$projectId', params: { projectId: project.id } })
      }
      caption="Projects"
      emptyTitle="No projects yet"
      emptyDescription="Create a blank project or start from a template."
      emptyAction={
        <Link to="/projects/new" className={buttonVariants()}>
          New project
        </Link>
      }
      toolbar={
        <div className="flex items-center gap-2">
          <Checkbox
            id="projects-include-archived"
            checked={includeArchived}
            onCheckedChange={(next) => onIncludeArchivedChange(next === true)}
          />
          <Label htmlFor="projects-include-archived" className="font-normal">
            Show archived projects
          </Label>
        </div>
      }
    />
  );
}
